import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {SuggestionStates} from '@github-ui/diff-lines/conversation-types'

import type {RevalidatedItemStatus, UnifiedBatchItem} from '../stores/slices/unified-batch'

type CommentMatch = {thread: Thread; comment: Comment}

/**
 * Indexes every thread comment by its `databaseId` so a batched item can be matched
 * back to the live thread/comment it was built from in O(1).
 */
function buildCommentIndex(threads: Thread[]): Map<number, CommentMatch> {
  const index = new Map<number, CommentMatch>()
  for (const thread of threads) {
    for (const comment of thread.commentsData?.comments ?? []) {
      if (comment.databaseId != null && !index.has(comment.databaseId)) {
        index.set(comment.databaseId, {thread, comment})
      }
    }
  }
  return index
}

/**
 * Computes the fresh outdated status for a single batched item from its live thread/comment.
 *
 * Returns `undefined` when the status is indeterminate so the caller omits the item and it keeps
 * its current status (fail-safe — revalidation never guesses).
 */
function deriveItemOutdated(item: UnifiedBatchItem, {thread, comment}: CommentMatch): boolean | undefined {
  // An ARC suggestion's staleness is carried per-comment by `suggestionState`, which stays reliable
  // for non-Copilot ARC (Code Scanning / Code Quality) whose *thread* does not outdate when the
  // suggestion goes stale — so thread-level `isOutdated` would wrongly report them current.
  if (item.kind === 'ccr-with-suggestion') {
    const state = comment.automatedComment?.suggestionState
    // No `suggestionState` on the comment is a data gap, not a signal — keep the current status.
    if (state === undefined) return undefined
    // Only `present` is committable. Every other state — `outdated`, `applied`, `no_suggestion`,
    // and transient `pending` / `error` — is non-committable, so mark it outdated;
    // `isCommittableSuggestion` then drops it from the apply payload while the batch keeps it as
    // Fix with Copilot context.
    return state !== SuggestionStates.Present
  }

  // Human suggestions and comment-only ARC items track thread-level outdatedness, which the server
  // settles before the user can Refresh. When every thread-level signal is absent the status is
  // indeterminate, so keep the item's current status rather than collapsing to `false` and wrongly
  // clearing an already-outdated item.
  return deriveThreadOutdated(thread)
}

/**
 * Reads thread-level outdatedness as a tri-state: `undefined` when no signal is present (keep the
 * current status), otherwise the union of the three optional signals (matches `getThreadIsOutdated`).
 */
function deriveThreadOutdated(thread: Thread): boolean | undefined {
  const signals = [thread.positioning?.isOutdated, thread.isOutdated, thread.subject?.isOutdated]
  if (signals.every(signal => signal === undefined)) return undefined
  return signals.some(Boolean)
}

/**
 * Produces per-item outdated statuses for the batch from the live threads, replacing the old
 * batch-wide cascade. Items are matched by `commentDatabaseId`; any item whose thread/comment is no
 * longer present, or whose status is indeterminate, is omitted so it keeps its current status. The
 * result is never destructive — the store's `revalidateItems` only toggles `isOutdated` and never
 * drops items.
 */
export function deriveRevalidatedStatuses(items: UnifiedBatchItem[], threads: Thread[]): RevalidatedItemStatus[] {
  if (items.length === 0 || threads.length === 0) return []

  const index = buildCommentIndex(threads)
  const statuses: RevalidatedItemStatus[] = []

  for (const item of items) {
    const match = index.get(item.commentDatabaseId)
    if (!match) continue

    const isOutdated = deriveItemOutdated(item, match)
    if (isOutdated === undefined) continue

    statuses.push({commentDatabaseId: item.commentDatabaseId, suggestionIndex: item.suggestionIndex, isOutdated})
  }

  return statuses
}
